import { Data } from "@angular/router";

export const DEFAULT_NO_THANKS_ACCENT_COLOR = "#5fd3b0";

const PRICE_TOKEN_PATTERN = /^\[(currency_actual|currency_beautify|currency_node):[^\]]+\]$/;
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
const UNRESOLVED_PRICE_TOKEN_PATTERN = /\[currency_(actual|beautify|node):/;

export function resolveNoThanksVariant(variant: unknown): 0 | 1 {
  return Number(variant) === 1 ? 1 : 0;
}

export function isValidHttpUrl(url: unknown): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const parsedUrl = new URL(url.trim());
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch {
    return false;
  }
}

export function isValidPriceToken(value: unknown): boolean {
  if (!value || typeof value !== "string") {
    return false;
  }

  return PRICE_TOKEN_PATTERN.test(value.trim());
}

export function isValidHexColor(value: unknown): boolean {
  if (!value || typeof value !== "string") {
    return false;
  }

  return HEX_COLOR_PATTERN.test(value.trim());
}

export function resolveHexColor(value: unknown, fallback = DEFAULT_NO_THANKS_ACCENT_COLOR): string {
  return isValidHexColor(value) ? String(value).trim() : fallback;
}

export function getTranslatedValue(translations: Data | undefined, lang: string): string {
  const value = translations?.[lang];
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

export function getTranslatedValueWithEnglishFallback(
  translations: Data | undefined,
  lang: string
): string {
  const localized = getTranslatedValue(translations, lang);
  if (localized) {
    return localized;
  }

  return getTranslatedValue(translations, "en");
}

export function getNonEmptyBulletPoints(
  bulletPoints: Data | undefined,
  lang: string
): string[] {
  const bullets = bulletPoints?.[lang];
  if (!Array.isArray(bullets)) {
    return [];
  }

  return bullets
    .filter((bullet): bullet is string => typeof bullet === "string")
    .map((bullet) => bullet.trim())
    .filter(Boolean);
}

export function getDefaultVariantContent(noThanksDetails: Data): Data {
  const nestedDefault = noThanksDetails?.variants?.default;
  if (nestedDefault) {
    return nestedDefault;
  }

  return {
    title: noThanksDetails?.title,
    description: noThanksDetails?.description,
    cta_label: noThanksDetails?.cta_label,
    no_thanks: noThanksDetails?.no_thanks,
  };
}

export function sanitizeResolvedPriceValue(
  resolved: unknown,
  rawToken: string
): string {
  if (!resolved || typeof resolved !== "string") {
    return "";
  }

  const trimmed = resolved.trim();
  if (!trimmed || UNRESOLVED_PRICE_TOKEN_PATTERN.test(trimmed)) {
    return "";
  }

  if (trimmed === rawToken.trim()) {
    return "";
  }

  return trimmed;
}
